import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { listCompliance, type ComplianceEvent } from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort } from '../../lib/format';
import { usePageChrome } from '../../admin/pageChrome';
import { useEventPaths } from '../../admin/routeBase';
import { useAdminScope } from '../../admin/scope';
import { useCopy } from '../../copy/CopyProvider';
import './AdminPages.css';

/**
 * Every event on the platform.
 *
 * ── WHY THIS IS NOT THE ORGANIZER DASHBOARD ──────────────────────────────────────────
 *
 * The obvious move was to mount OrganizerDashboard at /admin/events and call it reuse.
 * It would have been wrong: that screen reads GET /api/events/mine, which is scoped to
 * organizer_id. An admin would have seen only the events they personally created, under
 * a menu item labelled "All events". Reuse has to be reuse of something that answers the
 * question the screen asks.
 *
 * GET /api/admin/compliance?filter=all is the one admin read that returns every event
 * INCLUDING drafts, with its organizer and its publishing state — which is exactly the
 * operator's list. No new endpoint was needed.
 *
 * It is a LIST, not a second event editor. Clicking through goes to the same EventManage
 * the organizer portal uses, mounted under /admin.
 */

const STATUS_COPY: Record<string, string> = {
  draft: 'Draft',
  published: 'On sale',
  live: 'Live',
  closed: 'Closed',
  cancelled: 'Cancelled',
  postponed: 'Postponed',
};

type Filter = 'all' | 'draft' | 'live';

export function AdminEvents() {
  const paths = useEventPaths();
  const t = useCopy();
  const { organizationId } = useAdminScope();
  const [events, setEvents] = useState<ComplianceEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  // Page chrome may be copy-backed: these are static presentation labels. Breadcrumb
  // ENTITY names stay data — an event's title is what the event is called, not wording.
  usePageChrome({
    title: t('admin.events.title', 'Events'),
    subtitle: t('admin.events.subtitle', 'Every event on the platform, drafts included.'),
    actions: <Link to={paths.create} className="button button--primary">+ Create event</Link>,
  }, [paths.create, t]);

  useEffect(() => {
    let cancelled = false;
    listCompliance('all')
      .then((rows) => { if (!cancelled) setEvents(rows); })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load events.');
      });
    return () => { cancelled = true; };
  }, []);

  const shown = useMemo(() => {
    if (!events) return null;
    if (filter === 'draft') return events.filter((e) => e.eventStatus === 'draft');
    if (filter === 'live') return events.filter((e) => e.eventStatus === 'published' || e.eventStatus === 'live');
    return events;
  }, [events, filter]);

  return (
    <div className="adm-page">
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {/* Honesty about scope. Events carry an organization_id, but the admin event read
          does not filter on it yet, so this list is platform-wide whatever the header
          selector says. Saying so is better than a header that implies a filter nobody
          applied. */}
      {organizationId && (
        <p className="adm-lede adm-scope-note">
          Showing every organization. Organization scope is not applied to this list yet.
        </p>
      )}

      <div className="adm-filters">
        {(['all', 'draft', 'live'] as Filter[]).map((f) => (
          <button
            key={f}
            type="button"
            className={`adm-tab${filter === f ? ' is-active' : ''}`}
            aria-pressed={filter === f}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : f === 'draft' ? 'Drafts' : 'On sale'}
            {events ? ` (${f === 'all' ? events.length
              : f === 'draft' ? events.filter((e) => e.eventStatus === 'draft').length
                : events.filter((e) => e.eventStatus === 'published' || e.eventStatus === 'live').length})` : ''}
          </button>
        ))}
      </div>

      {!events && !error && <p className="adm-lede">Loading events…</p>}

      {shown && shown.length === 0 && (
        <p className="adm-lede">No events match this filter.</p>
      )}

      <div className="adm-list">
        {(shown ?? []).map((event) => (
          <article className="net-row" key={event.eventId}>
            <div className="net-row__main">
              <Link to={paths.manage(event.eventId)} className="adm-event__title">
                {event.title}
              </Link>
              <span className="adm-event__meta">
                {event.organizer.name || event.organizer.email}
                {event.venue ? ` · ${event.venue}` : ''}
              </span>
              <div className="net-row__badges">
                <span className="badge badge--muted">
                  {STATUS_COPY[event.eventStatus] ?? event.eventStatus}
                </span>
                {event.eventDate && (
                  <span className="badge badge--muted">{formatDateShort(event.eventDate)}</span>
                )}
                {/* Publishing state, not a compliance verdict — the Compliance queue is
                    where the checklist is actually worked. */}
                {event.blockedFromPublishing && (
                  <span className="badge badge--pending">Compliance blocked</span>
                )}
              </div>
            </div>
            <div className="net-row__actions">
              <Link to={paths.manage(event.eventId)} className="button button--secondary adm-btn-sm">
                Manage
              </Link>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
